import { FC, isValidElement } from 'react';

import { SkelentonSVGProps } from './SkeletonAnimation.types';
import { colors } from '@common/helpers';

const SkeletonAnimation: FC<SkelentonSVGProps> = ({
  animate = true,
  animateBegin,
  backgroundColor = colors.seafoam600,
  backgroundOpacity = 1,
  baseUrl = '',
  beforeMask = null,
  children,
  foregroundColor = colors.seafoam800,
  foregroundOpacity = 1,
  gradientDirection = 'left-right',
  gradientRatio = 2,
  interval = 0.25,
  rtl = false,
  speed = 1.2,
  style = {},
  ...props
}) => {
  const idClip = `-diff`;
  const idGradient = `-animated-diff`;
  const idAria = `-aria`;
  const rtlStyle = rtl ? { transform: 'scaleX(-1)' } : null;
  const keyTimes = `0; ${interval}; 1`;
  const dur = `${speed}s`;
  const gradientTransform = gradientDirection === 'top-bottom' ? 'rotate(90)' : undefined;

  return (
    <svg aria-labelledby={idAria} role="img" style={{ ...style, ...rtlStyle }} {...props}>
      {beforeMask && isValidElement(beforeMask) ? beforeMask : null}
      <rect
        role="presentation"
        x="0"
        y="0"
        width="100%"
        height="100%"
        clipPath={`url(${baseUrl}#${idClip})`}
        style={{ fill: `url(#${idGradient})` }}
      />
      <defs>
        {children}
        <linearGradient id={idGradient} gradientTransform={gradientTransform} role="linearGradient">
          <stop offset="0%" stopColor={backgroundColor} stopOpacity={backgroundOpacity}>
            {animate && (
              <animate
                attributeName="offset"
                values={`${-gradientRatio}; ${-gradientRatio}; 1`}
                keyTimes={keyTimes}
                dur={dur}
                repeatCount="indefinite"
                begin={animateBegin}
              />
            )}
          </stop>
          <stop offset="50%" stopColor={foregroundColor} stopOpacity={foregroundOpacity}>
            {animate && (
              <animate
                attributeName="offset"
                values={`${-gradientRatio / 2}; ${-gradientRatio / 2}; ${1 + gradientRatio / 2}`}
                keyTimes={keyTimes}
                dur={dur}
                repeatCount="indefinite"
                begin={animateBegin}
              />
            )}
          </stop>
          <stop offset="100%" stopColor={backgroundColor} stopOpacity={backgroundOpacity}>
            {animate && (
              <animate
                attributeName="offset"
                values={`0; 0; ${1 + gradientRatio}`}
                keyTimes={keyTimes}
                dur={dur}
                repeatCount="indefinite"
                begin={animateBegin}
              />
            )}
          </stop>
        </linearGradient>
      </defs>
    </svg>
  );
};

export default SkeletonAnimation;
